import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileText, Users, Wrench, Briefcase } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const AdminExport = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.admin_sub_role === 'superadmin';
  const [exporting, setExporting] = useState(null);

  const exportData = async (type) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can export data');
      return;
    }

    setExporting(type);
    
    try {
      // Simulate export
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(`${type} exported successfully`);
      
      // TODO: Implement actual export API call
      // const response = await axios.post(`${API}/admin/export/${type}`);
      // Download the file
      
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExporting(null);
    }
  };

  const exportOptions = [
    { type: 'users', label: 'All Users', icon: Users, description: 'Export complete user database', color: 'blue' },
    { type: 'tradies', label: 'Tradie Profiles', icon: Wrench, description: 'Export all tradie profiles and verifications', color: 'green' },
    { type: 'services', label: 'Service Listings', icon: Briefcase, description: 'Export all service listings', color: 'purple' },
    { type: 'jobs', label: 'Job Listings', icon: Briefcase, description: 'Export all job postings', color: 'cyan' },
    { type: 'quotes', label: 'Quotes', icon: FileText, description: 'Export all quotes and proposals', color: 'pink' },
    { type: 'logs', label: 'Activity Logs', icon: FileText, description: 'Export system activity logs', color: 'orange' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Data Export</h1>
          <p className="text-muted-foreground">Export platform data for analysis and backup</p>
        </div>

        {!isSuperAdmin && (
          <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
            <CardContent className="p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ Only Super Admin can export data. Contact a Super Admin if you need exports.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exportOptions.map((option) => (
            <Card key={option.type} className="border-2 border-border hover:shadow-lg transition-all">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 bg-${option.color}-100 dark:bg-${option.color}-900 rounded-xl flex items-center justify-center`}>
                    <option.icon className={`w-6 h-6 text-${option.color}-600 dark:text-${option.color}-400`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-foreground">{option.label}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{option.description}</p>
                <button
                  onClick={() => exportData(option.type)}
                  disabled={!isSuperAdmin || exporting === option.type}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting === option.type ? (
                    <>Exporting...</>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Export as CSV
                    </>
                  )}
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminExport;
